/**
 * Shared vocabulary for the whole engine.
 *
 * A *signal* is one scalar channel the UI can display (engine RPM, boost, an
 * O2 sensor's lambda). A *PID* is one request to the ECU that may yield several
 * signals at once. Derived signals have no PID behind them at all -- they are
 * computed from other signals.
 */

export type Category =
  | 'engine'
  | 'fuel'
  | 'air'
  | 'turbo'
  | 'emissions'
  | 'temperature'
  | 'electrical'
  | 'vehicle'
  | 'diesel'
  | 'hybrid'
  | 'status'
  | 'derived'
  | 'trip';

/** How a value should be rendered when we have no better idea. */
export interface DisplayHints {
  /** Sensible gauge floor. Not a hard clamp -- real data may exceed it. */
  min: number;
  /** Sensible gauge ceiling. */
  max: number;
  decimals: number;
  /** Values at or beyond this are "hot" (red zone) for gauges that show one. */
  warnAbove?: number;
  warnBelow?: number;
  /** Higher is better for scoring/colouring purposes (e.g. fuel economy). */
  betterWhen?: 'higher' | 'lower' | 'neutral';
}

export interface SignalDef extends DisplayHints {
  /** Globally unique, stable, snake_case. Persisted in dashboard configs. */
  key: string;
  name: string;
  /** Compact label for dense tables and small widgets. */
  short: string;
  unit: string;
  category: Category;
  description?: string;
  /** For enumerated signals (fuel system status, fuel type, ...). */
  enumLabels?: Record<number, string>;
  /** Bit-flag signals render as a list of set flags rather than a number. */
  bitLabels?: string[];
}

/** Decoded payload of one PID response: signal key -> value. */
export type Decoded = Record<string, number | undefined>;

export interface PidDef {
  /** OBD-II service, e.g. 0x01 for current data. */
  mode: number;
  pid: number;
  /** Expected number of data bytes after the echoed mode+pid. */
  bytes: number;
  signals: SignalDef[];
  decode: (d: Uint8Array) => Decoded;
  /** Default polling period in ms; the scheduler may be told otherwise. */
  defaultIntervalMs: number;
  /**
   * Whether this PID may ride along in a multi-PID request. Bitmap PIDs and
   * anything with a variable-length payload must be requested alone.
   */
  batchable: boolean;
}

export type ConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'handshaking'
  | 'identifying'
  | 'connected'
  | 'error';

/**
 * A byte pipe to the adapter. Web Bluetooth, a TCP socket in the Android shell,
 * a WebSocket bridge and the built-in simulator all implement this.
 */
export interface Transport {
  readonly kind: string;
  readonly label: string;
  connect(): Promise<void>;
  disconnect(): Promise<void>;
  write(data: Uint8Array): Promise<void>;
  /** Returns an unsubscribe function. */
  onData(cb: (chunk: Uint8Array) => void): () => void;
  onClose(cb: (reason?: string) => void): () => void;
  readonly connected: boolean;
}

export type FuelType = 'petrol' | 'diesel' | 'lpg' | 'cng' | 'ethanol' | 'hybrid' | 'electric' | 'unknown';

export interface VehicleProfile {
  /** Free text, only for display. */
  name: string;
  fuelType: FuelType;
  /** Set when the user overrides auto-detection. */
  fuelTypeLocked: boolean;
  /**
   * True once fuel-type detection has seeded the engine defaults. Prevents a
   * later reconnect from overwriting figures the driver has since edited.
   */
  autoTuned: boolean;
  /** Engine displacement in litres -- needed for speed-density MAF estimation. */
  displacementL: number;
  /** Volumetric efficiency 0..1.2, used by the same fallback. */
  volumetricEfficiency: number;
  /** Cylinder count -- turns injected mg/stroke into a mass flow. */
  cylinders: number;
  /** Kerb mass in kg, used for power/force estimates. */
  massKg: number;
  /**
   * Forward gears. The gear estimator never reports a higher gear than this,
   * and uses it to place gears it has not seen yet.
   */
  gearCount: number;
  /**
   * How the engine is coupled to the wheels, which decides what engine speed
   * over road speed can mean.
   *
   * A manual or a dual-clutch box is rigid once engaged, so the ratio is the
   * gear. A torque converter lets the engine run ahead of the gearbox by
   * anything up to a third until it locks up, so the same gear shows a spread
   * of ratios that only collapses onto the true one under lock-up. A CVT has
   * no discrete ratios at all and nothing can be estimated.
   */
  transmission: Transmission;
  /** Redline, for gauge scaling and shift lights. */
  redlineRpm: number;
  /** Above this the eco score starts penalising engine speed. */
  ecoShiftRpm: number;
  /** Fuel tank capacity in litres, to turn tank level % into litres. */
  tankCapacityL: number;
}

/** Drivetrain coupling. See `VehicleProfile.transmission`. */
export type Transmission = 'manual' | 'auto' | 'dct' | 'cvt';

export const TRANSMISSION_LABELS: Record<Transmission, string> = {
  manual: 'Manual',
  auto: 'Automatic (torque converter)',
  dct: 'Dual-clutch',
  cvt: 'CVT / stepless',
};

export const DEFAULT_VEHICLE: VehicleProfile = {
  name: 'My car',
  fuelType: 'unknown',
  fuelTypeLocked: false,
  autoTuned: false,
  displacementL: 2.0,
  volumetricEfficiency: 0.85,
  cylinders: 4,
  massKg: 1500,
  gearCount: 6,
  transmission: 'manual',
  redlineRpm: 6500,
  ecoShiftRpm: 2500,
  tankCapacityL: 55,
};

/** Physical constants for the two fuels we do real consumption maths for. */
export interface FuelConstants {
  /** Stoichiometric air/fuel mass ratio. */
  afr: number;
  /** Fuel density in grams per litre. */
  densityGPerL: number;
  /** Typical brake-specific fuel consumption, g/kWh -- rough power estimates. */
  bsfc: number;
}

export const FUEL_CONSTANTS: Record<FuelType, FuelConstants> = {
  petrol:   { afr: 14.7, densityGPerL: 745, bsfc: 280 },
  diesel:   { afr: 14.5, densityGPerL: 832, bsfc: 220 },
  lpg:      { afr: 15.5, densityGPerL: 540, bsfc: 300 },
  cng:      { afr: 17.2, densityGPerL: 190, bsfc: 290 },
  ethanol:  { afr: 9.0,  densityGPerL: 789, bsfc: 380 },
  hybrid:   { afr: 14.7, densityGPerL: 745, bsfc: 260 },
  electric: { afr: 1,    densityGPerL: 1,   bsfc: 0 },
  unknown:  { afr: 14.7, densityGPerL: 745, bsfc: 280 },
};
